import type React from 'react';
import { useState } from 'react';
import type { ExecutionDetail } from '../../types/agent-studio';
import { AgentStatusPill } from './AgentStatusPill';

export interface TraceLogExpandableProps {
  execution: ExecutionDetail;
  defaultExpanded?: boolean;
}

const INVOCATION_SOURCE_RU: Record<string, string> = {
  pipeline:        'Пайплайн',
  studio_test:     'Тест студии',
  task:            'Задача',
  eval_harness:    'Оценка',
  workspace_query: 'Рабочее пространство',
};

const MONO: React.CSSProperties = {
  fontFamily: "'Menlo', 'Consolas', 'Courier New', monospace",
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.5,
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  if (isToday) return `сегодня в ${hh}:${mm}`;
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${d.getDate()} ${months[d.getMonth()]} ${hh}:${mm}`;
}

function qualityLabel(qs: { coherence: number; completeness: number; conciseness: number } | Record<string, number>): string {
  const c = (qs as any).coherence ?? 0;
  const p = (qs as any).completeness ?? 0;
  const n = (qs as any).conciseness ?? 0;
  return `${c.toFixed(1)} / ${p.toFixed(1)} / ${n.toFixed(1)}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="t-eyebrow" style={{ marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function MonoBox({ text, placeholder }: { text: string; placeholder?: string }) {
  if (!text) {
    return (
      <span style={{ ...MONO, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
        {placeholder ?? '(пусто)'}
      </span>
    );
  }
  return (
    <div
      style={{
        ...MONO,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: 4,
        padding: '8px 10px',
        maxHeight: 200,
        overflowY: 'auto',
      }}
    >
      {text}
    </div>
  );
}

export function TraceLogExpandable({
  execution: ex,
  defaultExpanded = false,
}: TraceLogExpandableProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const shortId = ex.id.slice(-8);
  const dateStr = formatDate(ex.started_at);
  const sourceLabel = INVOCATION_SOURCE_RU[ex.invocation_source] ?? ex.invocation_source;
  const latencyStr = ex.latency_seconds != null ? `${ex.latency_seconds.toFixed(1)}с` : '—';
  const costStr = ex.cost_usd != null ? `$${ex.cost_usd.toFixed(5)}` : '—';
  const tokensStr = (ex.input_tokens != null && ex.output_tokens != null)
    ? `${ex.input_tokens} → ${ex.output_tokens}`
    : '—';
  const qualityStr = ex.quality_scores ? qualityLabel(ex.quality_scores) : '—';

  const jsonContext = Object.keys(ex.input_context ?? {}).length > 0
    ? JSON.stringify(ex.input_context, null, 2)
    : '';

  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 8 }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <AgentStatusPill status={ex.status as any} />

        <span className="t-mono-sm" style={{ color: 'var(--ink-secondary)', fontSize: 12 }}>
          #{shortId}
        </span>

        <span style={{ color: 'var(--ink-secondary)', fontSize: 13 }}>{dateStr}</span>

        <span
          className="t-micro"
          style={{ marginLeft: 'auto', color: 'var(--ink-tertiary)' }}
        >
          {sourceLabel}
        </span>

        <span
          style={{
            fontSize: 12,
            color: 'var(--ink-tertiary)',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            display: 'inline-block',
          }}
        >
          ▸
        </span>
      </div>

      {/* Summary row — always visible */}
      <div
        style={{
          display: 'flex',
          gap: 20,
          marginTop: 10,
          padding: '8px 0',
          borderTop: '1px solid var(--border)',
        }}
      >
        {[
          ['Длительность', latencyStr],
          ['Стоимость', costStr],
          ['Токены', tokensStr],
          ['Качество', qualityStr],
        ].map(([label, value]) => (
          <div key={label}>
            <div className="t-micro" style={{ fontSize: 9, marginBottom: 2 }}>{label}</div>
            <div className="t-mono-sm" style={{ fontSize: 13 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Expandable section */}
      {expanded && (
        <div style={{ marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 12 }}>

          {ex.error_message && (
            <Section title="Сообщение об ошибке">
              <div
                style={{
                  ...MONO,
                  background: 'var(--red-bg)',
                  border: '1px solid var(--red)',
                  borderRadius: 4,
                  padding: '8px 10px',
                  color: 'var(--red)',
                }}
              >
                {ex.error_message}
              </div>
            </Section>
          )}

          <Section title="Контекст входа">
            <MonoBox text={jsonContext} placeholder="(пусто)" />
          </Section>

          <Section title="Отрендеренный промпт">
            <MonoBox
              text={ex.rendered_prompt}
              placeholder="(не записано в Phase 1)"
            />
          </Section>

          <Section title="Вывод модели">
            <MonoBox
              text={ex.model_output}
              placeholder="(не записано в Phase 1)"
            />
          </Section>

          {ex.tool_calls.length > 0 && (
            <Section title="Вызовы инструментов">
              <MonoBox text={JSON.stringify(ex.tool_calls, null, 2)} />
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
